import React, { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Phone, ArrowUpDown, ChevronDown, ChevronUp, Clock, MessageSquare,
  Send, Loader2, X, User, Activity,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  NEW: "bg-blue-100 text-blue-700",
  CONTACTED: "bg-yellow-100 text-yellow-700",
  INTERESTED: "bg-green-100 text-green-700",
  QUALIFIED: "bg-purple-100 text-purple-700",
  WON: "bg-emerald-100 text-emerald-800",
  LOST: "bg-slate-100 text-slate-500",
};
const STATUSES = ["NEW", "CONTACTED", "INTERESTED", "QUALIFIED", "WON", "LOST"];

export default function LeadsTab({ adminToken }) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("newest");
  const [expandedPhone, setExpandedPhone] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort: sortBy, limit: "50" });
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      const res = await axios.get(`${API}/admin/leads?${params}`, { headers });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load leads");
    }
    setLoading(false);
  }, [statusFilter, sortBy, adminToken]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/stats/summary`, { headers });
      setStats(res.data);
    } catch { /* silent */ }
  }, [adminToken]);

  useEffect(() => { fetchLeads(); fetchStats(); }, [fetchLeads, fetchStats]);

  const updateStatus = async (phone, newStatus) => {
    try {
      await axios.patch(`${API}/admin/leads/${encodeURIComponent(phone)}/status`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${newStatus}`);
      fetchLeads();
      if (detailLead?.phone === phone) loadDetail(phone);
    } catch { toast.error("Failed to update status"); }
  };

  const addNote = async (phone) => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/leads/${encodeURIComponent(phone)}/notes`, { text: noteText }, { headers });
      toast.success("Note added");
      setNoteText("");
      loadDetail(phone);
    } catch { toast.error("Failed to add note"); }
    setSaving(false);
  };

  const loadDetail = async (phone) => {
    try {
      const res = await axios.get(`${API}/admin/leads/${encodeURIComponent(phone)}`, { headers });
      setDetailLead(res.data);
      setExpandedPhone(phone);
    } catch { toast.error("Failed to load lead detail"); }
  };

  const toggleExpand = (phone) => {
    if (expandedPhone === phone) {
      setExpandedPhone(null);
      setDetailLead(null);
    } else {
      loadDetail(phone);
    }
  };

  const latestTag = (lead) => {
    const calls = lead.calls || [];
    if (calls.length === 0) return null;
    return calls[calls.length - 1]?.ai_tag;
  };

  const latestSummary = (lead) => {
    const calls = lead.calls || [];
    if (calls.length === 0) return "";
    return calls[calls.length - 1]?.ai_summary || "";
  };

  const tagColor = (tag) => {
    if (tag === "interested") return "bg-green-100 text-green-700";
    if (tag === "maybe") return "bg-yellow-100 text-yellow-700";
    if (tag === "not_interested") return "bg-red-100 text-red-700";
    return "bg-slate-100 text-slate-500";
  };

  return (
    <div className="space-y-6" data-testid="leads-tab">
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="leads-stats">
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-xs text-slate-500">Total Leads</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.by_status?.NEW || 0}</p>
            <p className="text-xs text-slate-500">New</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.interested || 0}</p>
            <p className="text-xs text-slate-500">Interested</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.repeat_callers || 0}</p>
            <p className="text-xs text-slate-500">Repeat Callers</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.by_status?.WON || 0}</p>
            <p className="text-xs text-slate-500">Won</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="leads-filters">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="requests">Most Requests</SelectItem>
            <SelectItem value="score">Highest Score</SelectItem>
            <SelectItem value="loss">Highest Loss</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500 ml-auto">{total} lead{total !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No leads yet. Leads appear after "Talk to Expert" calls.</div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-8"></TableHead>
                <TableHead>Business</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead className="text-right">Loss/mo</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Requests</TableHead>
                <TableHead>AI Tag</TableHead>
                <TableHead>Summary</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <React.Fragment key={lead.phone + lead.place_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleExpand(lead.phone)}
                    data-testid={`lead-row-${lead.phone}`}
                  >
                    <TableCell className="w-8 px-2">
                      {expandedPhone === lead.phone ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-sm text-slate-900">{lead.business_name || "—"}</p>
                      <p className="text-xs text-slate-400">{lead.contact_name}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700 font-mono">{lead.phone}</TableCell>
                    <TableCell className="text-center font-bold text-sm">{lead.audit_score ?? "—"}</TableCell>
                    <TableCell className="text-right text-sm font-semibold text-red-600">
                      {lead.est_monthly_loss ? `$${lead.est_monthly_loss.toLocaleString()}` : "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`${STATUS_COLORS[lead.status] || STATUS_COLORS.NEW} text-[10px]`}>{lead.status}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`text-sm font-bold ${lead.request_count > 1 ? "text-amber-600" : "text-slate-500"}`}>
                        {lead.request_count || 1}×
                      </span>
                    </TableCell>
                    <TableCell>
                      {latestTag(lead) && <Badge className={`${tagColor(latestTag(lead))} text-[10px]`}>{latestTag(lead)}</Badge>}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <p className="text-xs text-slate-500 truncate">{latestSummary(lead)}</p>
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail */}
                  {expandedPhone === lead.phone && detailLead && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-slate-50 p-0">
                        <div className="p-5 space-y-4">
                          {/* Status changer */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs font-semibold text-slate-500 uppercase">Change Status:</span>
                            {STATUSES.map(s => (
                              <button key={s} onClick={(e) => { e.stopPropagation(); updateStatus(lead.phone, s); }}
                                className={`px-2.5 py-1 rounded text-[11px] font-bold transition ${detailLead.status === s ? "ring-2 ring-offset-1 ring-blue-400" : ""} ${STATUS_COLORS[s]}`}>
                                {s}
                              </button>
                            ))}
                          </div>

                          {/* Add note */}
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Add a note..."
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") addNote(lead.phone); }}
                              className="text-sm flex-1"
                              data-testid="lead-note-input"
                            />
                            <Button size="sm" onClick={() => addNote(lead.phone)} disabled={saving || !noteText.trim()} data-testid="lead-note-submit">
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            </Button>
                          </div>

                          {/* Activity timeline */}
                          <div>
                            <h4 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                              <Activity className="w-3.5 h-3.5" /> Activity Timeline
                            </h4>
                            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                              {[...(detailLead.activity_log || [])].reverse().map((a, i) => (
                                <div key={`${a.timestamp}-${i}`} className="flex items-start gap-2 text-xs border-l-2 border-slate-200 pl-3 py-1">
                                  <span className="text-slate-400 shrink-0 w-28">{new Date(a.timestamp).toLocaleString()}</span>
                                  <Badge variant="outline" className="text-[9px] shrink-0">{a.type}</Badge>
                                  <span className="text-slate-600">{a.detail}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Call transcripts */}
                          {(detailLead.calls || []).length > 0 && (
                            <div>
                              <h4 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" /> Call Records ({detailLead.calls.length})
                              </h4>
                              <div className="space-y-2">
                                {[...(detailLead.calls || [])].reverse().map((call, ci) => (
                                  <CallRecord key={`${call.timestamp}-${ci}`} call={call} />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function CallRecord({ call }) {
  const [expanded, setExpanded] = useState(false);
  const tag = call.ai_tag || "";
  const tagColor = tag === "interested" ? "text-green-600" : tag === "maybe" ? "text-amber-600" : "text-red-500";

  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs text-slate-400">{new Date(call.timestamp).toLocaleString()}</span>
          <span className="text-xs text-slate-400">{call.duration_seconds}s</span>
          {tag && <Badge variant="outline" className={`text-[9px] ${tagColor}`}>{tag}</Badge>}
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      {call.ai_summary && <p className="text-xs text-slate-600 mt-1">{call.ai_summary}</p>}
      {expanded && call.transcript && (
        <pre className="mt-2 text-xs text-slate-500 bg-slate-50 p-3 rounded max-h-[200px] overflow-y-auto whitespace-pre-wrap font-mono">
          {call.transcript}
        </pre>
      )}
    </div>
  );
}
